'use client';

import { useState, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { Check } from 'lucide-react';
import { ForfaitSelector, ForfaitOption } from './ForfaitSelector';
import { UserCountSelector } from './UserCountSelector';

interface PackFixeVoipCardProps {
  isAnnual: boolean;
}

// Données des forfaits Pack Fixe VOIP
const FORFAITS: ForfaitOption[] = [
  { id: 'start', label: 'Forfait Start', priceMonthly: 19.9, priceAnnual: 199 },
  { id: 'pro', label: 'Forfait Pro', priceMonthly: 39.9, priceAnnual: 399 },
];

// Destinations selon le forfait
const DESTINATIONS: Record<string, string> = {
  start: 'Fixes & Mobiles en France Métropolitaine',
  pro: 'Fixes & Mobiles en France Métropolitaine, DOM-TOM et International',
};

// Features avec placeholder pour les valeurs dynamiques
const getFeatures = (userCount: number, destination: string) => [
  '1 Numéro fixe France (01, 02, 03, 04, 05, 09 et DOM-TOM) ou conservez le vôtre avec la portabilité',
  `<strong>${userCount} utilisateur${userCount > 1 ? 's' : ''} inclus</strong>`,
  `Appels entrants et sortants illimités vers <strong>${destination}</strong>`,
  'Abonnement sans engagement',
  'Standard Virtuel Interactif avec toutes les fonctionnalités incluses (Message d\'accueil, musique d\'attente, renvoi d\'appels en cascade ou en simultané, configuration des horaires, etc)',
  'Journal d\'appel CRM avec tags d\'appels',
  'Espace client en ligne web & mobile',
  'Application Mobile (iOS & Android), Ordinateur (PC & MAC)',
  'Activation instantanée 100% en ligne',
];

export function PackFixeVoipCard({ isAnnual }: PackFixeVoipCardProps) {
  const router = useRouter();
  
  const [selectedForfait, setSelectedForfait] = useState('start');
  const [userCount, setUserCount] = useState(1);

  // Récupérer le forfait sélectionné
  const currentForfait = useMemo(() => {
    return FORFAITS.find((f) => f.id === selectedForfait) || FORFAITS[0];
  }, [selectedForfait]);

  // Prix unitaire
  const unitPrice = isAnnual ? currentForfait.priceAnnual : currentForfait.priceMonthly;

  // Prix total (multiplié par le nombre d'utilisateurs)
  const totalPrice = unitPrice !== null ? unitPrice * userCount : null;

  // Destination selon le forfait
  const destination = DESTINATIONS[selectedForfait] || DESTINATIONS.start;

  // Features avec les valeurs dynamiques
  const features = useMemo(() => {
    return getFeatures(userCount, destination);
  }, [userCount, destination]);

  const handleSubscribe = () => {
    router.push(`/commande?type=telephonieIp`);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-primaryBlue overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="bg-primaryBlue text-white p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
            <img
              src="/pack-fixe.png"
              alt="Pack Fixe VoIP"
              className="w-10 h-10 object-contain"
            />
          </div>
          <div>
            <h3 className="text-xl font-bold">Pack Fixe VoIP</h3>
            <p className="text-sm opacity-90">Je développe</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex-1 flex flex-col">
        {/* Sélecteur de forfait */}
        <div className="mb-6 text-center">
          <p className="text-sm text-gray-600 mb-3">Choisissez votre forfait :</p>
          <ForfaitSelector
            options={FORFAITS}
            selectedId={selectedForfait}
            onSelect={setSelectedForfait}
            isAnnual={isAnnual}
            colorScheme="blue"
          />
        </div>

        {/* Sélecteur nombre d'utilisateurs */}
        <div className="mb-6 text-center">
          <UserCountSelector
            value={userCount}
            onChange={setUserCount}
            min={1}
            max={50}
            colorScheme="blue"
          />
        </div>

        {/* Prix */}
        <div className="mb-6 text-center">
          {totalPrice !== null ? (
            <>
              <p className="text-4xl font-bold text-primaryBlue">
                {totalPrice.toFixed(2).replace('.', ',')}€
                <span className="text-lg font-normal text-gray-600">
                  {isAnnual ? ' HT/an' : ' HT/mois'}
                </span>
              </p>
            </>
          ) : (
            <p className="text-gray-500">Non disponible</p>
          )}
        </div>

        {/* Features */}
        <ul className="space-y-3 mb-6 flex-1">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primaryBlue flex-shrink-0 mt-0.5" />
              <span
                className="text-sm text-gray-700 text-left"
                dangerouslySetInnerHTML={{ __html: feature }}
              />
            </li>
          ))}
        </ul>

        {/* CTA */}
        <button
          onClick={handleSubscribe}
          disabled={totalPrice === null}
          className="w-full py-3 px-6 bg-primaryBlue text-white font-semibold rounded-full hover:bg-primaryBlue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Souscrire
        </button>
      </div>
    </div>
  );
}
